/**
 * Event bus for managing instance-scoped events.
 * Each map instance has its own isolated event bus.
 */
export class EventBus {
  private instanceId: string;
  private events: Map<string, Set<Function>> = new Map();

  constructor(instanceId: string) {
    this.instanceId = instanceId;
  }

  /**
   * Register an event listener
   * @param event - Event name
   * @param callback - Callback function
   */
  public on(event: string, callback: Function): void {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    
    this.events.get(event)!.add(callback);
    
    if (import.meta.env.NODE_ENV === 'development') {
      console.log(`[EventBus:${this.instanceId}] Listener registered for: ${event}`);
    }
  }

  /**
   * Unregister an event listener
   * @param event - Event name
   * @param callback - Callback function
   */
  public off(event: string, callback: Function): void {
    const listeners = this.events.get(event);
    
    if (listeners) {
      listeners.delete(callback);
      
      if (import.meta.env.NODE_ENV === 'development') {
        console.log(`[EventBus:${this.instanceId}] Listener removed for: ${event}`);
      }
      
      // Clean up empty event sets
      if (listeners.size === 0) {
        this.events.delete(event);
      }
    }
  }

  /**
   * Trigger an event and call all registered listeners
   * @param event - Event name
   * @param data - Event data
   */
  public trigger(event: string, data?: any): void {
    const listeners = this.events.get(event);
    
    if (!listeners || listeners.size === 0) {
      return;
    }

    if (import.meta.env.NODE_ENV === 'development') {
      console.log(`[EventBus:${this.instanceId}] Event triggered: ${event}`, data);
    }

    // Call all listeners with error handling
    listeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`[EventBus:${this.instanceId}] Error in event listener for ${event}:`, error);
      }
    });
  }

  /**
   * Get all registered event names
   */
  public getEventNames(): string[] {
    return Array.from(this.events.keys());
  }

  /**
   * Get listener count for an event
   * @param event - Event name
   */
  public getListenerCount(event: string): number {
    const listeners = this.events.get(event);
    return listeners ? listeners.size : 0;
  }

  /**
   * Clear all event listeners
   */
  public clearAll(): void {
    this.events.clear();
    
    if (import.meta.env.NODE_ENV === 'development') {
      console.log(`[EventBus:${this.instanceId}] All listeners cleared`);
    }
  }

  /**
   * Destroy the event bus and clean up all resources
   * This is an alias for clearAll() for backward compatibility
   */
  public destroy(): void {
    this.clearAll();
    
    if (import.meta.env.NODE_ENV === 'development') {
      console.log(`[EventBus:${this.instanceId}] Destroyed`);
    }
  }

  /**
   * Check if an event has listeners
   * @param event - Event name
   */
  public hasListeners(event: string): boolean {
    return this.getListenerCount(event) > 0;
  }
}
